import { isAxiosError } from 'axios';

/**
 * Error normalisation for the NestJS default error shape.
 *
 * The API is single-language English and returns messages, not codes, so the
 * frontend's whole job is to reduce whatever came back — a string, an array of
 * class-validator strings, an HTML error page from a proxy, or nothing at all
 * because the network dropped — to one sentence that can go straight into a
 * toast or an inline field error.
 */

/** The NestJS default error body. */
export interface NestErrorBody {
  statusCode?: number;
  /** A single sentence, or one entry per failed class-validator constraint. */
  message?: string | string[];
  /** The HTTP reason phrase ("Bad Request"). Never shown to a user. */
  error?: string;
}

/** Why a request failed, when it failed before the server answered. */
export type ApiFailureKind = 'response' | 'network' | 'timeout' | 'canceled';

/** Messages used when the server said nothing useful. */
const STATUS_FALLBACKS: ReadonlyMap<number, string> = new Map([
  [400, 'That request was not valid.'],
  [401, 'Your session has expired. Sign in again.'],
  [403, 'You do not have permission to do that.'],
  [404, 'That could not be found. It may have been deleted.'],
  [409, 'That conflicts with something that already exists.'],
  [413, 'That upload is too large.'],
  [415, 'That file type is not supported.'],
  [422, 'That request could not be processed.'],
  [429, 'Too many requests. Wait a moment and try again.'],
]);

const GENERIC_CLIENT_MESSAGE = 'That request could not be completed.';
const GENERIC_SERVER_MESSAGE =
  'Something went wrong on the server. Try again in a moment.';
const NETWORK_MESSAGE =
  'Could not reach the server. Check your connection and try again.';
const TIMEOUT_MESSAGE = 'The server took too long to respond. Try again.';
const CANCELED_MESSAGE = 'That request was cancelled.';

/** Give a fragment terminal punctuation so joined entries read as sentences. */
const asSentence = (value: string): string => {
  const trimmed = value.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
};

const fallbackForStatus = (status: number | null): string => {
  if (status === null) {
    return GENERIC_CLIENT_MESSAGE;
  }

  return (
    STATUS_FALLBACKS.get(status) ??
    (status >= 500 ? GENERIC_SERVER_MESSAGE : GENERIC_CLIENT_MESSAGE)
  );
};

/**
 * Reduce a NestJS error body to one display sentence.
 *
 * `message` carries everything worth showing. `error` is only ever the HTTP
 * reason phrase, which is protocol vocabulary rather than English a reviewer
 * should read, so an empty `message` falls through to a written fallback.
 */
export const toDisplayMessage = (
  body: unknown,
  status: number | null,
): string => {
  if (typeof body === 'string' && body.trim() !== '') {
    /*
     * A bare string body is almost always a proxy or dev-server error page
     * rather than the API. Only keep it when it is plausibly a sentence — an
     * HTML document would be unreadable in a toast.
     */
    const trimmed = body.trim();
    if (!trimmed.startsWith('<') && trimmed.length <= 200) {
      return asSentence(trimmed);
    }
    return fallbackForStatus(status);
  }

  if (typeof body !== 'object' || body === null) {
    return fallbackForStatus(status);
  }

  const { message } = body as NestErrorBody;

  if (Array.isArray(message)) {
    const sentences = [
      ...new Set(
        message
          .filter((entry): entry is string => typeof entry === 'string')
          .map((entry) => entry.trim())
          .filter((entry) => entry !== '')
          .map(asSentence),
      ),
    ];

    return sentences.length > 0 ? sentences.join(' ') : fallbackForStatus(status);
  }

  if (typeof message === 'string' && message.trim() !== '') {
    return asSentence(message);
  }

  return fallbackForStatus(status);
};

/**
 * A request failure, already reduced to something a person can read.
 *
 * `message` is the display string. `status` and `body` are kept for the callers
 * that need to branch — a 409 rendered inline on a "name already used" field,
 * say — but nothing should ever have to re-derive the wording.
 */
export class ApiError extends Error {
  /** HTTP status, or `null` when the request never reached the server. */
  readonly status: number | null;

  /** Whether the server answered at all, and if not, why. */
  readonly kind: ApiFailureKind;

  /** The raw response body, for callers that need more than the message. */
  readonly body: unknown;

  constructor(
    message: string,
    options: {
      status?: number | null;
      kind?: ApiFailureKind;
      body?: unknown;
      cause?: unknown;
    } = {},
  ) {
    super(message, options.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'ApiError';
    this.status = options.status ?? null;
    this.kind = options.kind ?? 'response';
    this.body = options.body;
  }
}

/** Narrow an unknown thrown value to {@link ApiError}. */
export const isApiError = (value: unknown): value is ApiError =>
  value instanceof ApiError;

/**
 * Turn anything thrown by axios into an {@link ApiError}.
 *
 * Exported separately from the interceptor so it can be unit-tested without an
 * HTTP stack, and reused anywhere else a raw axios rejection is caught.
 */
export const normalizeApiError = (error: unknown): ApiError => {
  if (isApiError(error)) {
    return error;
  }

  if (isAxiosError(error)) {
    if (error.code === 'ERR_CANCELED') {
      return new ApiError(CANCELED_MESSAGE, { kind: 'canceled', cause: error });
    }

    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError(TIMEOUT_MESSAGE, { kind: 'timeout', cause: error });
    }

    const { response } = error;

    if (response === undefined) {
      return new ApiError(NETWORK_MESSAGE, { kind: 'network', cause: error });
    }

    return new ApiError(toDisplayMessage(response.data, response.status), {
      status: response.status,
      kind: 'response',
      body: response.data,
      cause: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError(asSentence(error.message || GENERIC_CLIENT_MESSAGE), {
      cause: error,
    });
  }

  return new ApiError(GENERIC_CLIENT_MESSAGE, { cause: error });
};
